// ADTS framing for AAC-LC (ISO/IEC 13818-7 §6.2 / 14496-3 §1.A.2).
//
// The uplink asks WebCodecs for raw AAC (`aac.format: 'aac'`) and frames it
// here rather than trusting the encoder's own ADTS output: the header is then
// the same on every platform, and the header fields are ours to test.
export const ADTS_HEADER_LEN = 7;
export const AAC_LC_OBJECT_TYPE = 2;
export const AAC_SAMPLES_PER_FRAME = 1024;
const SAMPLE_RATES = [
    96000, 88200, 64000, 48000, 44100, 32000, 24000, 22050, 16000, 12000, 11025, 8000, 7350,
];
export function samplingFrequencyIndex(sampleRate) {
    const idx = SAMPLE_RATES.indexOf(sampleRate);
    if (idx < 0)
        throw new Error(`AAC: unsupported sample rate ${sampleRate}`);
    return idx;
}
export function sampleRateFromIndex(idx) {
    const sr = SAMPLE_RATES[idx];
    if (sr === undefined)
        throw new Error(`AAC: invalid sampling_frequency_index ${idx}`);
    return sr;
}
/** Build the 7-byte ADTS header (no CRC) for one raw AAC frame. */
export function adtsHeader(params, rawFrameLen) {
    const frameLen = rawFrameLen + ADTS_HEADER_LEN;
    if (frameLen > 0x1fff)
        throw new Error(`AAC: frame too large for ADTS (${frameLen} bytes)`);
    if (params.channels < 1 || params.channels > 7) {
        throw new Error(`AAC: unsupported channel count ${params.channels}`);
    }
    const profile = (params.objectType ?? AAC_LC_OBJECT_TYPE) - 1;
    const sfi = samplingFrequencyIndex(params.sampleRate);
    const ch = params.channels;
    const h = new Uint8Array(ADTS_HEADER_LEN);
    h[0] = 0xff;
    h[1] = 0xf1; // syncword low nibble, MPEG-4, layer 00, protection_absent=1
    h[2] = ((profile & 0x03) << 6) | ((sfi & 0x0f) << 2) | ((ch >> 2) & 0x01);
    h[3] = ((ch & 0x03) << 6) | ((frameLen >> 11) & 0x03);
    h[4] = (frameLen >> 3) & 0xff;
    h[5] = ((frameLen & 0x07) << 5) | 0x1f; // buffer fullness 0x7FF (VBR)
    h[6] = 0xfc; // buffer fullness low bits, number_of_raw_data_blocks - 1 = 0
    return h;
}
/** Prefix one raw AAC access unit with its ADTS header. */
export function toAdts(params, raw) {
    const out = new Uint8Array(ADTS_HEADER_LEN + raw.length);
    out.set(adtsHeader(params, raw.length), 0);
    out.set(raw, ADTS_HEADER_LEN);
    return out;
}
/**
 * Split a buffer of concatenated ADTS frames (one audio PES payload) into
 * raw access units. Stops at the first byte that is not a syncword; a
 * truncated trailing frame is dropped.
 */
export function parseAdts(buf) {
    const frames = [];
    let off = 0;
    while (off + ADTS_HEADER_LEN <= buf.length) {
        if (buf[off] !== 0xff || (buf[off + 1] & 0xf0) !== 0xf0)
            break;
        const protectionAbsent = buf[off + 1] & 0x01;
        const objectType = ((buf[off + 2] >> 6) & 0x03) + 1;
        const sfi = (buf[off + 2] >> 2) & 0x0f;
        const channels = ((buf[off + 2] & 0x01) << 2) | ((buf[off + 3] >> 6) & 0x03);
        const frameLen = ((buf[off + 3] & 0x03) << 11) | (buf[off + 4] << 3) | ((buf[off + 5] >> 5) & 0x07);
        const headerLen = protectionAbsent ? 7 : 9;
        if (frameLen < headerLen || off + frameLen > buf.length)
            break;
        frames.push({
            objectType,
            sampleRate: sampleRateFromIndex(sfi),
            channels,
            raw: buf.subarray(off + headerLen, off + frameLen),
        });
        off += frameLen;
    }
    return frames;
}
/** MPEG-4 AudioSpecificConfig (2 bytes) — the AudioDecoder `description`. */
export function audioSpecificConfig(params) {
    const ot = params.objectType ?? AAC_LC_OBJECT_TYPE;
    const sfi = samplingFrequencyIndex(params.sampleRate);
    return new Uint8Array([
        ((ot & 0x1f) << 3) | ((sfi >> 1) & 0x07),
        ((sfi & 0x01) << 7) | ((params.channels & 0x0f) << 3),
    ]);
}
//# sourceMappingURL=adts.js.map